'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { AIModel, AIModelId } from './CreateGameScreen';

interface AIModelSelectorProps {
  models: AIModel[];
  selectedModel: AIModelId;
  onSelect: (id: AIModelId) => void;
  dailyCredits: number;
  purchasedCredits: number;
}

const qualityBars: Record<string, number> = {
  'Básica': 1,
  'Alta': 2,
  'Máxima': 3,
};

export default function AIModelSelector({
  models,
  selectedModel,
  onSelect,
  dailyCredits,
  purchasedCredits,
}: AIModelSelectorProps) {
  const totalCredits = dailyCredits + purchasedCredits;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-foreground">Modelo de IA</h2>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="BoltIcon" size={11} className="text-warning" />
          {totalCredits} créditos
        </span>
      </div>

      <div className="space-y-3">
        {models.map((model) => {
          const isSelected = selectedModel === model.id;
          const canAfford = totalCredits >= model.creditsPerGeneration;
          const bars = qualityBars[model.quality] ?? 1;

          return (
            <button
              key={`model-${model.id}`}
              onClick={() => onSelect(model.id)}
              className={`
                w-full text-left p-4 rounded-2xl border transition-all duration-150 active:scale-[0.99]
                ${isSelected
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/40 shadow-lg shadow-primary/10'
                  : 'border-border bg-card hover:border-primary/30 hover:bg-muted/20'
                }
              `}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full transition-colors ${isSelected ? 'bg-primary shadow-sm shadow-primary' : 'bg-muted-foreground/40'}`} />
                  <span className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>{model.name}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {isSelected && (
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary text-white">
                      Ativo
                    </span>
                  )}
                  <StatusBadge variant={model.badge} size="sm" label={model.accessType} />
                </div>
              </div>

              <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{model.description}</p>

              {/* Quality indicator */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-xs text-muted-foreground">Qualidade:</span>
                <div className="flex gap-1">
                  {[1, 2, 3].map((b) => (
                    <div
                      key={`quality-${model.id}-${b}`}
                      className={`w-6 h-1.5 rounded-full transition-colors ${b <= bars ? (isSelected ? 'bg-primary' : 'bg-primary/60') : 'bg-muted'}`}
                    />
                  ))}
                </div>
                <span className="text-xs font-medium text-foreground">{model.quality}</span>
              </div>

              {/* Features */}
              <ul className="space-y-1 mb-3">
                {model.features.map((feat) => (
                  <li key={`feat-${model.id}-${feat}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="CheckIcon" size={11} className="text-success flex-shrink-0" />
                    {feat}
                  </li>
                ))}
              </ul>

              {/* Cost */}
              <div className={`
                flex items-center justify-between px-3 py-2 rounded-xl
                ${isSelected ? 'bg-primary/20' : 'bg-muted/40'}
              `}>
                <span className="text-xs text-muted-foreground">Por geração</span>
                <span className={`text-sm font-bold tabular-nums flex items-center gap-1 ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                  <Icon name="BoltIcon" size={13} className="text-warning" />
                  {model.creditsPerGeneration} créditos
                </span>
              </div>

              {!canAfford && (
                <p className="mt-2 text-xs text-warning flex items-center gap-1">
                  <Icon name="ExclamationCircleIcon" size={11} />
                  Créditos insuficientes — será redirecionado para comprar
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Credit summary */}
      <div className="mt-4 p-4 rounded-xl bg-muted/30 border border-border space-y-2">
        <p className="text-xs font-semibold text-muted-foreground">Os teus créditos</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Icon name="SunIcon" size={12} className="text-warning" />
            Créditos diários
          </span>
          <span className="text-xs font-mono font-semibold text-foreground tabular-nums">{dailyCredits} / 75</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Icon name="CurrencyEuroIcon" size={12} className="text-accent" />
            Créditos comprados
          </span>
          <span className="text-xs font-mono font-semibold text-foreground tabular-nums">{purchasedCredits}</span>
        </div>
        <div className="h-px bg-border" />
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-foreground">Total disponível</span>
          <span className="text-xs font-mono font-bold text-foreground tabular-nums">{dailyCredits + purchasedCredits}</span>
        </div>
      </div>
    </div>
  );
}